import { type ReactNode } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft, X } from 'lucide-react'

type UsageEvent = {
  id: string
  name: string | null
  event_type: string | null
  date: string | null
  city: string | null
  state: string | null
  status: string | null
}

type UsageOrganization = {
  id: string
  name: string
  events: UsageEvent[]
}

type Filters = {
  year: string
  start_date: string
  end_date: string
  value: string
}

type Props = {
  field: string
  label: string
  filters: Filters
  years: number[]
  value_options: string[]
  answered: number
  distinct_count: number
  top_values: { value: string; count: number }[]
  organizations: UsageOrganization[] | null
}

const ANY = '__any__'

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchReportsField({
  field,
  label,
  filters,
  years,
  value_options,
  answered,
  distinct_count,
  top_values,
  organizations,
}: Props) {
  const hasFilters = Object.values(filters).some(Boolean)
  const max = top_values[0]?.count ?? 0

  function applyFilters(next: Partial<Filters>) {
    const merged = { ...filters, ...next }
    router.get(
      '/admin/research/reports/field',
      {
        field,
        ...Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')),
      },
      { preserveState: true },
    )
  }

  return (
    <>
      <Head title={`Research - ${label} Analytics`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3">
          <Link href="/admin/research/reports">
            <ArrowLeft className="mr-1 size-4" />
            Back to Event Analytics
          </Link>
        </Button>

        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">{label}</h2>
          <Badge variant="secondary">{answered} recorded</Badge>
          <Badge variant="secondary">
            {distinct_count} distinct {distinct_count === 1 ? 'value' : 'values'}
          </Badge>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-2">
          <Select
            value={filters.value || ANY}
            onValueChange={value => applyFilters({ value: value === ANY ? '' : value })}
          >
            <SelectTrigger className="w-48" size="sm">
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent position="popper" align="start">
              <SelectGroup>
                <SelectItem value={ANY}>{label}: All</SelectItem>
                {value_options.map(option => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select
            value={filters.year || ANY}
            onValueChange={value => applyFilters({ year: value === ANY ? '' : value })}
          >
            <SelectTrigger className="w-32" size="sm">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent position="popper" align="start">
              <SelectGroup>
                <SelectItem value={ANY}>Year: All</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <div className="space-y-1">
            <Label htmlFor="field-start-date" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="field-start-date"
              type="date"
              className="h-8 w-38"
              value={filters.start_date}
              onChange={e => applyFilters({ start_date: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="field-end-date" className="text-xs text-muted-foreground">
              To
            </Label>
            <Input
              id="field-end-date"
              type="date"
              className="h-8 w-38"
              value={filters.end_date}
              onChange={e => applyFilters({ end_date: e.target.value })}
            />
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.get('/admin/research/reports/field', { field })}
            >
              <X className="mr-1 size-3.5" />
              Clear
            </Button>
          )}
        </div>

        <div className="mt-6 grid grid-cols-3 gap-6">
          <Card className="self-start">
            <CardHeader>
              <div className="flex items-baseline justify-between">
                <CardTitle>
                  {distinct_count > top_values.length
                    ? `Top ${top_values.length} values`
                    : 'All values'}
                </CardTitle>
                {distinct_count > top_values.length && (
                  <span className="text-xs text-muted-foreground">
                    of {distinct_count}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {top_values.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No data recorded for these filters.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {top_values.map(({ value, count }) => {
                    const share = answered > 0 ? Math.round((count / answered) * 100) : 0
                    const active = filters.value === value
                    return (
                      <li key={value}>
                        <button
                          type="button"
                          onClick={() => applyFilters({ value: active ? '' : value })}
                          className={`group -mx-2 block w-[calc(100%+1rem)] rounded-lg px-2 py-1 text-left transition-colors ${
                            active ? 'bg-primary/10 ring-1 ring-primary/20' : 'hover:bg-muted/60'
                          }`}
                          title={active ? 'Clear filter' : `Filter to ${value}`}
                        >
                          <div className="flex items-baseline justify-between gap-2 text-sm">
                            <span className="truncate font-medium group-hover:underline">
                              {value}
                            </span>
                            <span className="shrink-0 text-muted-foreground">
                              {count} · {share}%
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-muted">
                            <div
                              className="h-1.5 rounded-full bg-primary"
                              style={{ width: `${max > 0 ? (count / max) * 100 : 0}%` }}
                            />
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <div className="col-span-2">
            {organizations === null ? (
              <p className="rounded-2xl border border-black/10 bg-card p-8 text-center text-sm text-muted-foreground shadow-xs">
                Pick a {label.toLowerCase()} (or click a bar) to see which organizations use it.
              </p>
            ) : (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold tracking-tight">
                  Organizations using &ldquo;{filters.value}&rdquo;
                </h3>
                {organizations.map(org => (
                  <div key={org.id}>
                    <div className="mb-2 flex items-baseline gap-2">
                      <Link
                        href={`/admin/research/organizations/${org.id}`}
                        className="font-semibold tracking-tight hover:underline"
                      >
                        {org.name}
                      </Link>
                      <span className="text-sm text-muted-foreground">
                        {org.events.length} {org.events.length === 1 ? 'event' : 'events'}
                      </span>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Event</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {org.events.map(event => (
                            <TableRow key={event.id}>
                              <TableCell className="font-medium">
                                <Link
                                  href={`/admin/research/events/${event.id}`}
                                  className="hover:underline"
                                >
                                  {event.name || 'Untitled'}
                                </Link>
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {event.event_type || '—'}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {formatDate(event.date)}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {[event.city, event.state].filter(Boolean).join(', ') || '—'}
                              </TableCell>
                              <TableCell>
                                {event.status ? (
                                  <Badge variant="secondary">{event.status}</Badge>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
                {organizations.length === 0 && (
                  <p className="rounded-2xl border border-black/10 bg-card p-8 text-center text-sm text-muted-foreground shadow-xs">
                    No organizations found for this value and date range.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

ResearchReportsField.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
